import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  ArrowUpDown,
  Users,
  Trash2,
  Sparkles,
  Award,
  Check
} from 'lucide-react';
import { doc, setDoc, deleteDoc } from 'firebase/firestore';
import { ALL_STICKERS, STICKER_MAP } from '../constants';

interface FriendProfile {
  userId: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  owned?: string[];
  duplicates?: string[];
}

interface TradeBoardProps {
  user: any;
  owned: Set<string>;
  duplicates: string[];
  acceptedFriends: FriendProfile[];
  trades: any[];
  onUpdateInventory?: (owned: Set<string>, duplicates: string[]) => Promise<void>;
  db: any;
}

export const TradeBoard = ({
  user,
  owned,
  duplicates,
  acceptedFriends,
  trades,
  onUpdateInventory,
  db
}: TradeBoardProps) => {
  const myUid = user?.uid;

  // Local state managers
  const [selectedOfferId, setSelectedOfferId] = useState('');
  const [selectedWantId, setSelectedWantId] = useState('');
  const [tradeMessage, setTradeMessage] = useState<{ text: string; type: 'success' | 'err' } | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  // Filter unique duplicates to offer
  const uniqueDuplicates = Array.from(new Set(duplicates)).sort((a, b) => {
    const sA = STICKER_MAP[a];
    const sB = STICKER_MAP[b];
    if (!sA || !sB) return 0;
    return sA.num - sB.num;
  });

  // Filter missing stickers to want
  const missingStickers = ALL_STICKERS.filter(s => !owned.has(s.id)).sort((a, b) => a.num - b.num);

  // Divide trade lists
  const myPending = trades.filter(t => t.userId === myUid && t.status === 'pending');
  const myCompleted = trades.filter(t => t.userId === myUid && t.status === 'completed');

  const friendsPending = trades.filter(
    t => t.userId !== myUid &&
         t.status === 'pending' &&
         acceptedFriends.some(f => f.userId === t.userId)
  );

  // Find symmetrical matching swaps
  const matchSwaps: Array<{ mine: any; friend: any }> = [];
  myPending.forEach(m => {
    friendsPending.forEach(f => {
      if (m.offeredStickerId === f.wantedStickerId && m.wantedStickerId === f.offeredStickerId) {
        matchSwaps.push({ mine: m, friend: f });
      }
    });
  });

  // Triggers
  const handlePostTrade = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOfferId || !selectedWantId) {
      setTradeMessage({ text: "Please select both a spare to offer and seeking item.", type: 'err' });
      return;
    }
    if (!duplicates.includes(selectedOfferId)) {
      setTradeMessage({ text: "You don't own this duplicate copy in inventory anymore.", type: 'err' });
      return;
    }

    setActionLoading('create');
    setTradeMessage(null);

    try {
      const id = `trade_${myUid}_${Date.now()}`;
      await setDoc(doc(db, 'trades', id), {
        id,
        userId: myUid,
        userName: user?.displayName || user?.email?.split('@')[0] || 'Collector',
        userEmail: user?.email || '',
        offeredStickerId: selectedOfferId,
        wantedStickerId: selectedWantId,
        status: 'pending',
        createdAt: new Date().toISOString()
      });

      setTradeMessage({ text: "Exchange offer published successfully!", type: 'success' });
      setSelectedOfferId('');
      setSelectedWantId('');
    } catch (err) {
      console.error("Failed to post trade document:", err);
      setTradeMessage({ text: "Database connection timed out. Try again.", type: 'err' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleCancelTrade = async (tradeId: string) => {
    setActionLoading(tradeId);
    setTradeMessage(null);
    try {
      await deleteDoc(doc(db, 'trades', tradeId));
      setTradeMessage({ text: "Trade offer removed.", type: 'success' });
    } catch (err) {
      console.error("Cancel trade failed:", err);
      setTradeMessage({ text: "Error cancelling trade. Try again.", type: 'err' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleAcceptTrade = async (trade: any) => {
    if (!onUpdateInventory) return;
    const { id, offeredStickerId, wantedStickerId, userName } = trade;

    if (!duplicates.includes(wantedStickerId)) {
      setTradeMessage({
        text: `You need a duplicate copy of ${STICKER_MAP[wantedStickerId]?.name || wantedStickerId} to fulfill this swap.`,
        type: 'err'
      });
      return;
    }

    setActionLoading(id);
    setTradeMessage(null);

    try {
      const nextOwned = new Set(owned);
      nextOwned.add(offeredStickerId);

      const nextDuplicates = [...duplicates];
      const idx = nextDuplicates.indexOf(wantedStickerId);
      if (idx > -1) {
        nextDuplicates.splice(idx, 1);
      }

      await setDoc(doc(db, 'trades', id), {
        ...trade,
        status: 'completed',
        partnerId: myUid,
        partnerName: user?.displayName || user?.email?.split('@')[0] || 'Collector',
        partnerEmail: user?.email || '',
        updatedAt: new Date().toISOString()
      });

      await onUpdateInventory(nextOwned, nextDuplicates);

      setTradeMessage({
        text: `Swapped! You exchanged ${STICKER_MAP[wantedStickerId]?.name} for ${STICKER_MAP[offeredStickerId]?.name} with ${userName}!`,
        type: 'success'
      });
    } catch (err) {
      console.error("Accept trade error:", err);
      setTradeMessage({ text: "Database error claiming swap.", type: 'err' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleClaimCompleted = async (trade: any) => {
    if (!onUpdateInventory) return;
    const { id, offeredStickerId, wantedStickerId, partnerName } = trade;

    setActionLoading(`claim_${id}`);
    setTradeMessage(null);

    try {
      const nextOwned = new Set(owned);
      nextOwned.add(wantedStickerId);

      const nextDuplicates = [...duplicates];
      const idx = nextDuplicates.indexOf(offeredStickerId);
      if (idx > -1) {
        nextDuplicates.splice(idx, 1);
      }

      await deleteDoc(doc(db, 'trades', id));
      await onUpdateInventory(nextOwned, nextDuplicates);

      setTradeMessage({
        text: `Collected ${STICKER_MAP[wantedStickerId]?.name}! Swapped with ${partnerName}!`,
        type: 'success'
      });
    } catch (err) {
      console.error("Collect trade error:", err);
      setTradeMessage({ text: "Could not sync collection.", type: 'err' });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSolveMatchSwap = async (m: any, f: any) => {
    if (!onUpdateInventory) return;
    setActionLoading(`match_${m.id}`);
    setTradeMessage(null);

    try {
      const nextOwned = new Set(owned);
      nextOwned.add(f.offeredStickerId);

      const nextDuplicates = [...duplicates];
      const idx = nextDuplicates.indexOf(m.offeredStickerId);
      if (idx > -1) {
        nextDuplicates.splice(idx, 1);
      }

      await setDoc(doc(db, 'trades', f.id), {
        ...f,
        status: 'completed',
        partnerId: myUid,
        partnerName: user?.displayName || user?.email?.split('@')[0] || 'Collector',
        partnerEmail: user?.email || '',
        updatedAt: new Date().toISOString()
      });

      await deleteDoc(doc(db, 'trades', m.id));
      await onUpdateInventory(nextOwned, nextDuplicates);

      setTradeMessage({
        text: `Perfect Swap Complete! Traded ${STICKER_MAP[m.offeredStickerId]?.name} for ${STICKER_MAP[f.offeredStickerId]?.name} with ${f.userName}!`,
        type: 'success'
      });
    } catch (err) {
      console.error("Solving perfect match failed:", err);
      setTradeMessage({ text: "Perfect match resolution error.", type: 'err' });
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      {/* Left Panel: Form & Active listings */}
      <div className="space-y-6">
        <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-5 shadow-xl space-y-4 text-left">
          <div className="flex items-center gap-2 border-b border-slate-805 pb-3">
            <ArrowUpDown size={16} className="text-amber-500" />
            <h3 className="font-impact text-lg text-white uppercase tracking-wider">Configure Exchange Offer</h3>
          </div>

          {tradeMessage && (
            <div className={`p-3 rounded text-center text-[10px] font-mono border ${
              tradeMessage.type === 'success' 
                ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                : 'bg-red-500/10 text-red-400 border-red-500/20'
            }`}>
              {tradeMessage.text}
            </div>
          )}

          <form onSubmit={handlePostTrade} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-[9px] font-mono text-slate-400 uppercase font-black">Offer Duplicate Spare</label>
              <select
                value={selectedOfferId}
                onChange={(e) => setSelectedOfferId(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 rounded px-3 py-2 text-xs text-white outline-none transition-colors font-mono"
              >
                <option value="">-- Choose Spare --</option>
                {uniqueDuplicates.map((id) => {
                  const sticker = STICKER_MAP[id];
                  if (!sticker) return null;
                  const count = duplicates.filter(d => d === id).length;
                  return (
                    <option key={id} value={id}>
                      {sticker.type === 'special' ? sticker.icon : '⚽'} {sticker.name} ({sticker.rarity} x{count})
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[9px] font-mono text-slate-400 uppercase font-black">Request Missing Sticker</label>
              <select
                value={selectedWantId}
                onChange={(e) => setSelectedWantId(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 focus:border-amber-500 rounded px-3 py-2 text-xs text-white outline-none transition-colors font-mono"
              >
                <option value="">-- Choose Wanted --</option>
                {missingStickers.map((sticker) => (
                  <option key={sticker.id} value={sticker.id}>
                    ⚽ #{sticker.num} {sticker.name} ({sticker.rarity})
                  </option>
                ))}
              </select>
            </div>

            <button
              type="submit"
              disabled={actionLoading === 'create' || !selectedOfferId || !selectedWantId}
              className="w-full py-2.5 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-mono font-black text-xs uppercase rounded tracking-wider shadow transition-all active:scale-[0.98] disabled:opacity-50 disabled:scale-100 disabled:pointer-events-none cursor-pointer flex items-center justify-center gap-1 leading-snug"
            >
              {actionLoading === 'create' ? 'Posting...' : 'Post Exchange Offer 🤝'}
            </button>
          </form>
        </div>

        {/* My Active Lists */}
        <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-5 shadow-xl space-y-4 text-left">
          <div className="border-b border-slate-800 pb-2">
            <h3 className="font-mono text-xs font-black uppercase tracking-wider text-slate-350">
              My Active Trade Offers ({myPending.length})
            </h3>
          </div>

          {myPending.length === 0 ? (
            <div className="py-8 text-center bg-slate-950/20 border border-slate-900 border-dashed rounded font-mono text-[9px] text-slate-500">
              No Pending Exchanges
            </div>
          ) : (
            <div className="space-y-3">
              {myPending.map((trade) => {
                const offerS = STICKER_MAP[trade.offeredStickerId];
                const wantS = STICKER_MAP[trade.wantedStickerId];
                if (!offerS || !wantS) return null;

                return (
                  <div key={trade.id} className="bg-slate-950/40 border border-slate-900 rounded-lg p-3 space-y-2 text-[10px] font-mono">
                    <div className="flex justify-between items-start gap-1">
                      <div className="space-y-1">
                        <div>
                          <span className="text-[8px] text-slate-500 uppercase block leading-none">Offering:</span>
                          <span className="text-white font-bold">{offerS.name}</span>
                        </div>
                        <div>
                          <span className="text-[8px] text-amber-500 uppercase block leading-none">Seeking:</span>
                          <span className="text-amber-400 font-bold">{wantS.name}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => handleCancelTrade(trade.id)}
                        disabled={actionLoading === trade.id}
                        className="p-1 px-2 border border-red-500/20 hover:border-red-500 text-red-400 rounded transition-all text-[8px] uppercase tracking-wide cursor-pointer font-bold shrink-0"
                      >
                        {actionLoading === trade.id ? '...' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Right Content Area: Inbox, Perfect matches, and listings board */}
      <div className="lg:col-span-2 space-y-6">

        {/* Completed Trade mail drop */}
        {myCompleted.length > 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-[#1e293b] border-2 border-emerald-500/80 rounded-xl p-5 shadow-[0_0_20px_rgba(16,185,129,0.15)] space-y-3.5 text-left"
          >
            <div className="flex items-center gap-2">
              <Sparkles size={14} className="text-emerald-400 animate-bounce" />
              <h4 className="font-mono text-xs font-black uppercase tracking-wider text-emerald-500">
                🎉 Completed Trades Inbox ({myCompleted.length})
              </h4>
            </div>
            <p className="text-[10px] text-slate-400 font-mono leading-relaxed mt-1">
              Your trade listings have been fulfilled by other collectors! Claim your reward files to integrate them straight into your album:
            </p>

            <div className="space-y-2.5 max-h-[250px] overflow-y-auto pr-1">
              {myCompleted.map((trade) => {
                const offerS = STICKER_MAP[trade.offeredStickerId];
                const wantS = STICKER_MAP[trade.wantedStickerId];
                if (!offerS || !wantS) return null;

                return (
                  <div
                    key={trade.id}
                    className="p-3 bg-slate-950/50 border border-slate-900 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs"
                  >
                    <div className="space-y-1">
                      <div className="text-[10px] font-mono leading-none">
                        <span className="text-slate-350 font-bold block mb-1">Fulfilled by: {trade.partnerName}</span>
                        <span className="text-slate-500">You exchanged your duplicate </span>
                        <span className="text-red-400 font-bold">{offerS.name}</span>
                        <span className="text-slate-500"> to receive </span>
                        <span className="text-emerald-400 font-bold">{wantS.name}</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleClaimCompleted(trade)}
                      disabled={actionLoading === `claim_${trade.id}`}
                      className="sm:w-auto w-full px-4 py-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-mono font-black text-[10px] uppercase rounded tracking-wider shadow transition-all active:scale-[0.98] cursor-pointer shrink-0"
                    >
                      {actionLoading === `claim_${trade.id}` ? 'Collecting...' : 'Collect Sticker 🎁'}
                    </button>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        {/* Dynamic perfect match finder */}
        {matchSwaps.length > 0 && (
          <div className="bg-[#1e293b] border-2 border-amber-500/80 rounded-xl p-5 shadow-[0_0_20px_rgba(245,158,11,0.15)] text-left space-y-4">
            <div className="flex items-center gap-2">
              <Award size={16} className="text-amber-400 animate-pulse" />
              <h3 className="font-impact text-lg text-white uppercase tracking-wider">Perfect Symmetrical Match Found!</h3>
            </div>
            <p className="text-[10px] text-slate-400 font-mono leading-relaxed mt-0.5">
              Automated trade pairing resolved! A friend has exactly what you are seeking and wants exactly what you are offering. Execute swap below:
            </p>

            <div className="space-y-3">
              {matchSwaps.map((match, idx) => {
                const mineOffer = STICKER_MAP[match.mine.offeredStickerId];
                const mineWant = STICKER_MAP[match.mine.wantedStickerId];
                if (!mineOffer || !mineWant) return null;

                return (
                  <div
                    key={idx}
                    className="bg-slate-950/60 border border-amber-500/20 rounded-lg p-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs font-mono"
                  >
                    <div className="space-y-1">
                      <span className="text-[9px] text-amber-400 font-bold uppercase block leading-none mb-1">Exchange with {match.friend.userName}</span>
                      <span className="text-slate-300 font-bold block leading-relaxed">
                        You swap duplicate {mineOffer.name} to receive their duplicate {mineWant.name}!
                      </span>
                    </div>
                    <button
                      onClick={() => handleSolveMatchSwap(match.mine, match.friend)}
                      disabled={actionLoading === `match_${match.mine.id}`}
                      className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 text-slate-950 font-mono font-black text-[10px] uppercase rounded tracking-wider shadow transition-all active:scale-[0.98] cursor-pointer shrink-0"
                    >
                      {actionLoading === `match_${match.mine.id}` ? 'Resolving...' : 'Swap Swifter 🤝'}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Main Board Listings */}
        <div className="bg-[#1e293b] border border-slate-800 rounded-xl p-5 shadow-xl space-y-4 text-left">
          <div className="flex items-center justify-between border-b border-slate-800/80 pb-3">
            <div className="flex items-center gap-2">
              <Users size={18} className="text-amber-500" />
              <h3 className="font-impact text-xl text-white uppercase tracking-wider">Accepted Friends Exchange Board</h3>
            </div>
            <span className="text-[9px] font-mono uppercase bg-amber-500/10 text-amber-400 border border-amber-500/20 px-2 py-0.5 rounded-full font-bold">
              {friendsPending.length} Active Offers
            </span>
          </div>

          {acceptedFriends.length === 0 ? (
            <div className="py-12 bg-slate-950/10 border border-slate-850 border-dashed rounded-lg flex flex-col items-center justify-center text-center p-6 font-mono text-slate-500">
              <Users size={32} className="text-slate-600 mb-2" />
              <span className="text-xs font-bold uppercase block leading-none mb-1">No connected collectors!</span>
              <span className="text-[10px] max-w-sm mt-0.5">Connect with other collectors under the Directory tab to enable live trade boards!</span>
            </div>
          ) : friendsPending.length === 0 ? (
            <div className="py-12 bg-slate-950/10 border border-slate-850 border-dashed rounded-lg flex flex-col items-center justify-center text-center p-6 font-mono text-slate-500">
              <ArrowUpDown size={32} className="text-slate-600 mb-2" />
              <span className="text-xs font-bold uppercase block leading-none mb-1">No Current Listings</span>
              <span className="text-[10px] max-w-[340px] mt-0.5">Your friends haven't posted any exchange offers yet. Check back later or make your own!</span>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {friendsPending.map((trade) => {
                const offerS = STICKER_MAP[trade.offeredStickerId];
                const wantS = STICKER_MAP[trade.wantedStickerId];
                if (!offerS || !wantS) return null;

                const canFulfill = duplicates.includes(trade.wantedStickerId);

                return (
                  <div
                    key={trade.id}
                    className="bg-slate-950/40 border border-slate-900 hover:border-amber-500/20 rounded-xl p-4 transition-all flex flex-col justify-between space-y-4 relative group hover:shadow-lg"
                  >
                    <div className="flex items-start justify-between min-w-0">
                      <div className="flex flex-col min-w-0 leading-tight">
                        <span className="font-bold text-white text-xs truncate max-w-[140px]">
                          {trade.userName}
                        </span>
                        <span className="text-[8px] font-mono text-slate-500 truncate max-w-[140px] mt-0.5">
                          {""}
                        </span>
                      </div>
                      <span className="text-[7px] font-mono uppercase bg-amber-500/10 text-amber-400 border border-amber-500/20 px-1.5 py-0.5 rounded-full font-bold">
                        swap exchange
                      </span>
                    </div>

                    <div className="bg-slate-950/65 p-3 rounded-lg text-[10px] font-mono space-y-2 border border-slate-900/40">
                      <div>
                        <span className="text-[7px] text-slate-500 uppercase block mb-0.5">They are offering:</span>
                        <span className="text-white font-bold flex items-center gap-1">
                          {offerS.type === 'special' ? offerS.icon : '⚽'} {offerS.name}
                          <span className="text-[8px] font-normal text-slate-500 uppercase tracking-tighter">({offerS.rarity})</span>
                        </span>
                      </div>
                      <div>
                        <span className="text-[7px] text-amber-500 uppercase block mb-0.5">They want in return:</span>
                        <span className="text-amber-400 font-bold flex items-center gap-1">
                          ⚽ {wantS.name}
                          <span className="text-[8px] font-normal text-slate-500 uppercase tracking-tighter tracking-tight tracking-normal">({wantS.rarity})</span>
                        </span>
                      </div>
                    </div>

                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleAcceptTrade(trade)}
                        disabled={!canFulfill || actionLoading === trade.id}
                        className={`w-full py-2 font-mono font-bold text-[9px] uppercase rounded transition-all flex items-center justify-center gap-1 cursor-pointer font-black text-slate-950 ${
                          canFulfill 
                            ? 'bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400' 
                            : 'bg-slate-900 text-slate-500 border border-slate-805 cursor-not-allowed'
                        }`}
                      >
                        <ArrowUpDown size={10} />
                        {actionLoading === trade.id ? 'Swapping...' : 'Accept Exchange & Swap 🤝'}
                      </button>
                      {!canFulfill && (
                        <span className="text-[7px] font-mono text-red-400 text-center leading-normal mt-0.5 uppercase tracking-wide">
                          ⚠️ You do not own a spare duplicate copy of {wantS.name}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

    </div>
  );
};
